/*
 * Nivo internally uses d3 format for label/values formatting
 * using the same for custom formats - https://github.com/d3/d3-format
 */
import { DatumValue } from '@nivo/core'
import { format } from 'd3-format'
import { useCallback } from 'react'
import { useIntlContext } from '~/components/IntlProviders.context'
import { useCurrencySettingsQuery } from '~/api/currency'
import { useMsg } from '~/common/localizationUtils'

const CURRENCY_FORMAT = '($.3s'
const NUMBERS_FORMAT = ',d'

export const getFormatSpecifier = (type: 'currency' | 'number'): string =>
  type === 'currency' ? CURRENCY_FORMAT : NUMBERS_FORMAT

export const getFormattedValue = (formatSpecifier: string, value: number) => format(formatSpecifier)(value)

export const useChartFormat = (type: 'currency' | 'number', symbol: boolean = true) => {
  const intl = useIntlContext().intl
  const currencySettingsQuery = useCurrencySettingsQuery()
  const currencyCode = currencySettingsQuery.data?.base_currency_code || 'USD'

  const options: Intl.NumberFormatOptions = {
    notation: 'compact',
    compactDisplay: 'short',
    minimumFractionDigits: 0,
    maximumFractionDigits: 2
  }

  if (type === 'currency') {
    if (symbol) {
      Object.assign(options, { style: 'currency', currencyDisplay: 'narrowSymbol', currency: currencyCode })
    } else {
      Object.assign(options, { style: 'currency', currencyDisplay: 'code', currency: currencyCode })
    }
  }

  return useCallback(
    (value: DatumValue) => {
      if (value === null || value === undefined) {
        return ''
      }
      return new Intl.NumberFormat(intl.locale, options).format(unformat(value))
    },
    [intl, currencyCode]
  )
}

/**
Append currency code to chart title
 */
export const useFormattedTitle = (title: string, format: 'currency' | 'number') => {
  const msg = useMsg()
  const currencySettingsQuery = useCurrencySettingsQuery()
  const currencyCode = currencySettingsQuery.data?.base_currency_code || 'USD'
  if (format === 'currency') {
    return msg('dashboards.chart.spend_currency_code_suffix', { title, currency_code: currencyCode })
  }
  return `${title}`
}

/**
 * Unfortunately sometimes the "number" returned by server side is "formatted" and is not in the way we want.
 * So we have to clean the "formation" to get the "number" as a number as the Intl library sees it.
 */
function unformat(value: DatumValue): number {
  if (typeof value === 'string') {
    // remove all constitutes except digits and period
    return Number.parseFloat(value.replace(/[^0-9.]/g, ''))
  }
  if (typeof value === 'number') {
    return value
  }
  return 0
}
